import {
  Badge,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useDispatch, useSelector } from "react-redux";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import AllEquipmentIcon from "../../assets/icons/All Equipment.svg";

import AddUserIcon from "../../assets/icons/addUser.svg";

import AllUsersIcon from "../../assets/icons/allUsersIcon.svg";
import { useUserAccountLogoutMutation } from "../../features/login/loginapi.services";
import { Navbar } from "../../shared";
import {
  AdminType,
  EventManagersType,
  isGridPolaris,
  itemNamePlural,
  itemTitlePlural,
  logout,
  MentorsType,
  user,
  USER_TYPES,
} from "../../utils/constant";
import Footer from "../Footer/Footer";
import MuiTooltipWrapper from "../Tooltip/Tooltip";
import { useGetFilterCountQuery } from "../../features/chat/chat.services";
import { Engineering, LocalShipping } from "@mui/icons-material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import {
  NAV_ROUTE_PERMISSIONS,
  usePermissions,
} from "../../permissions";
import useUserActivity from "../../hooks/useUserActivity";
import CustomDropdownMenu from "./CustomDropdownMenu";

export const isUserAllowed = (role) => {
  if (isGridPolaris) {
    return [user.admin, user.volunteer, user.event_manager, user.hta].includes(
      role
    );
  }
  return [user.admin, user.mentee, user.mentor].includes(role);
};

export default function Layout({ subheader }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userInfo = useSelector((state) => state.userInfo);
  const { profile } = useSelector((state) => state.profileInfo);
  const { pathname } = location;
  const from_event = searchParams.get("from") === "event";
  const role = userInfo.data.role || "";
  const is_event = searchParams.get("is_event") === "true";
  const [userAccountLogout] = useUserAccountLogoutMutation();
  const { canAny, isLoading: permissionsLoading } = usePermissions();
  const { data: counts } = useGetFilterCountQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const handleLogout = () => {
    logout({ userAccountLogout, dispatch, navigate });
  };

  const {
    snackbarOpen,
    warningMessage,
    handleSnackbarClose,
    isLoggedIn,
  } = useUserActivity({ onIdleLogout: handleLogout });

  // nav paths
  const dashboardPaths = ["/dashboard"];
  const programPaths = [
    "/create-programs",
    "/programs",
    "/program-details",
    "/update-program",
    "/events",
  ];
  const userPaths = [
    "/mentor-details",
    "/members",
    "/user",
  ];
  const operationsPaths = ["/defects", "/work-orders"];
  const calendarPaths = ["/calendar"];
  const messagePaths = ["/discussions"];
  const timesheetPaths = [
    "/adminTimeSheet",
    "/mentorProgramTimeSheet",
    "/menteeTimeSheet",
  ];
  const analyticsPath = ["/analytics"];

  const selectedTabStyle =
    "text-font-primary-main bg-background-primary-light font-semibold";

  const isPathSelected = (path) => pathname === path;

  const getMemberMenuItems = () =>
    [
      canAny(NAV_ROUTE_PERMISSIONS.ADD_USER) && {
        label: "Add user",
        icon: <img className="h-5 w-5" src={AddUserIcon} alt="AddUserIcon" />,
        command: () => navigate("/members/add?breadcrumbsType=createNewUser"),
        selected: isPathSelected("/members/add"),
        tooltip: "Fill out details to add a new user to the system",
        placement: "right",
      },
      canAny(NAV_ROUTE_PERMISSIONS.MEMBERS_LIST) && {
        label: "All Users",
        icon: <img className="h-5 w-5" src={AllUsersIcon} alt="AllUsersIcon" />,
        command: () => navigate("/members"),
        selected: pathname === "/members",
        tooltip: "View and manage all users registered in the system",
        placement: "right",
      },
    ].filter(Boolean);

  const isHta = role === user.hta;
  const canSeeDefectsMenu =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.DEFECTS);
  const canSeeWorkOrdersMenu =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.WORK_ORDERS);

  const OperationsMenuItems = () =>
    [
      canSeeDefectsMenu && {
        label: "Defects",
        icon: <FactCheckOutlinedIcon fontSize="small" />,
        command: () => navigate("/defects"),
        selected: isPathSelected("/defects") || pathname.startsWith("/defects/"),
        tooltip: "View and manage defects",
        placement: "right",
      },
      canSeeWorkOrdersMenu && {
        label: "Work Orders",
        icon: <Engineering fontSize="small" />,
        command: () => navigate("/work-orders"),
        selected:
          isPathSelected("/work-orders") || pathname.startsWith("/work-orders/"),
        tooltip: "View and manage work orders",
        placement: "right",
      },
    ].filter(Boolean);

  const getEquipmentMenuItems = () =>
    [
      canAny(NAV_ROUTE_PERMISSIONS.ADD_MOBILE_UNIT) && {
        label: "Add Mobile Unit",
        icon: <LocalShipping />,
        command: () => navigate("/mobileunit/add"),
        selected: isPathSelected("/mobileunit/add"),
        tooltip: "Register new mobile unit",
        placement: "right",
      },
      canAny(NAV_ROUTE_PERMISSIONS.MOBILE_UNITS_LIST) && {
        label: "All Mobile Units",
        icon: (
          <img
            className="h-5 w-5"
            src={AllEquipmentIcon}
            alt="AllEquipmentIcon"
          />
        ),
        command: () => navigate("/equipment"),
        selected: isPathSelected("/equipment"),
        tooltip: "View and manage all registered mobile units",
        placement: "right",
      },
      canAny(NAV_ROUTE_PERMISSIONS.INVENTORY) && {
        label: "Physical Count",
        icon: <FactCheckOutlinedIcon fontSize="small" />,
        command: () => navigate("/inventory-counts"),
        selected:
          isPathSelected("/inventory-counts") ||
          pathname.startsWith("/inventory-counts/"),
        tooltip: "Monthly physical inventory count",
        placement: "right",
      },
    ].filter(Boolean);

  const canSeeUsersMenu =
    !permissionsLoading && getMemberMenuItems().length > 0;
  const canSeeEquipmentMenu =
    !permissionsLoading && getEquipmentMenuItems().length > 0;
  const canSeePrograms =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.PROGRAMS);
  const canSeeCalendar =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.CALENDAR);
  const canSeeMessages =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.MESSAGES);
  const canSeeTimesheet =
    !permissionsLoading && canAny(NAV_ROUTE_PERMISSIONS.TIMESHEET);
  const canSeeAnalytics =
    !isHta &&
    !permissionsLoading &&
    canAny(NAV_ROUTE_PERMISSIONS.ANALYTICS);
  const canSeeHome =
    !isHta &&
    userInfo?.data?.is_registered &&
    !permissionsLoading &&
    canAny(NAV_ROUTE_PERMISSIONS.HOME);


  // Check if the user is registered and has appropriate access
  const shouldShowNav =
    !subheader &&
    userInfo?.data?.is_registered &&
    (profile?.approve_status === "accept" ||
      role === user.admin ||
      role === user.hta) &&
    userInfo.data.role !== user.super_admin;
  const selectedTab = userPaths.includes("/" + pathname.split("/")[1]);
  const isSelectedOperationsTab =
    operationsPaths.includes("/" + pathname.split("/")[1]) ||
    pathname.startsWith("/defects/") ||
    pathname.startsWith("/work-orders/");
  const isSelectedEquipmentTab =
    pathname === "/equipment" ||
    pathname === "/mobileunit/add" ||
    pathname.startsWith("/mobileunit/update") ||
    pathname === "/inventory-counts" ||
    pathname.startsWith("/inventory-counts/");
    
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header Section */}
      <div className="flex-shrink-0 mb-2">
        <Navbar />
        {shouldShowNav && (
          <div
            className="secondary-menu px-4 py-2 pr-6 sm:py-2 md:py-4"
            style={{ boxShadow: "4px 4px 25px 0px rgba(0, 0, 0, 0.05)" }}
          >
            <div className="overflow-x-auto">
              <ul
                className={`flex ${
                  !userInfo?.data?.is_registered
                    ? "ml-[150px]"
                    : "justify-center"
                } items-center p-2 md:p-0 mt-2 sm:mt-4 border border-gray-100 
              rounded-lg md:space-x-4 lg:space-x-8 rtl:space-x-reverse flex-row md:mt-0 md:border-0 min-w-max`}
              >
                {canSeeHome && (
                  <li
                    className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                      dashboardPaths.includes(pathname) ? selectedTabStyle : ""
                    }`}
                  >
                    <MuiTooltipWrapper
                      title="Key metrics & overview"
                      placement="top"
                    >
                      <span
                        onClick={() => navigate("/dashboard?type=yettojoin")}
                        className="block py-1 px-2 sm:py-2 sm:px-3 md:py-0 md:px-0 rounded md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                        aria-current="page"
                      >
                        Home
                      </span>
                    </MuiTooltipWrapper>
                  </li>
                )}

                {canSeePrograms && (
                <li
                  className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                    programPaths.includes("/" + pathname.split("/")[1]) &&
                    !is_event &&
                    !from_event
                      ? selectedTabStyle
                      : ""
                  }`}
                >
                  <MuiTooltipWrapper
                    title={`Manage all ${itemNamePlural}`}
                    placement="top"
                  >
                    <span
                      onClick={() => {
                        const pathURL = "/programs?page=1";
                        navigate(pathURL);
                      }}
                      className="block py-1 px-2 sm:py-2 sm:px-3 rounded md:hover:bg-transparent md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                    >
                      {itemTitlePlural}
                    </span>
                  </MuiTooltipWrapper>
                </li>
                )}
        
                {canSeeUsersMenu && (
                  <li
                    className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                      userPaths.includes("/" + pathname.split("/")[1])
                        ? selectedTabStyle
                        : ""
                    }`}
                  >
                    <CustomDropdownMenu
                      label="Users"
                      items={getMemberMenuItems()}
                      tooltip="Manage platform users"
                      placement="top"
                      isSelectedTab={selectedTab}
                    />
                  </li>
                )}

                {userInfo?.data?.is_registered && (
                  <>
                    {canSeeCalendar && (
                    <li
                      className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                        calendarPaths.includes("/" + pathname.split("/")[1])
                          ? selectedTabStyle
                          : ""
                      }`}
                    >
                      <MuiTooltipWrapper
                        title="View meeting schedule"
                        placement="top"
                      >
                        <span
                          onClick={() => navigate("/calendar")}
                          className="block py-1 px-2 sm:py-2 sm:px-3 rounded md:hover:bg-transparent md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                        >
                          Calendar
                        </span>
                      </MuiTooltipWrapper>
                    </li>
                    )}

                    {canSeeMessages && (
                    <li
                      className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                        messagePaths.includes(pathname) ? selectedTabStyle : ""
                      }`}
                    >
                      <MuiTooltipWrapper
                        title={
                          [user.mentee, user.volunteer].includes(role)
                            ? `Chat with ${AdminType}/${
                                role === user.mentee
                                  ? MentorsType
                                  : EventManagersType
                              }`
                            : "Chat with other users"
                        }
                        placement="top"
                      >
                        <Badge
                          badgeContent={counts?.unread_count || 0}
                          color="primary"
                          overlap="rectangular"
                          showZero={false}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#fff",
                            },
                          }}
                          invisible={pathname === "/discussions"}
                        >
                          <span
                            onClick={() => navigate("/discussions")}
                            className="block py-1 px-2 sm:py-2.5 sm:px-3 rounded md:hover:bg-transparent md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                          >
                            Messages
                          </span>
                        </Badge>
                      </MuiTooltipWrapper>
                    </li>
                    )}

                    {canSeeTimesheet && (
                    <li
                      className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                        timesheetPaths.includes(pathname)
                          ? selectedTabStyle
                          : ""
                      }`}
                    >
                      <MuiTooltipWrapper
                        title="Log or view hours"
                        placement="top"
                      >
                        <span
                          onClick={() =>
                            navigate(
                              [
                                USER_TYPES.MENTEE,
                                USER_TYPES.VOLUNTEER,
                              ].includes(role)
                                ? "/menteeTimeSheet"
                                : [
                                    USER_TYPES.MENTOR,
                                    USER_TYPES.EVENT_MANAGER,
                                  ].includes(role)
                                ? "/mentorProgramTimeSheet"
                                : "/adminTimeSheet",
                              { state: { role: role } }
                            )
                          }
                          className="block py-1 px-2 sm:py-2 sm:px-3 rounded md:hover:bg-transparent md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                        >
                          Timesheet
                        </span>
                      </MuiTooltipWrapper>
                    </li>
                    )}
                    {(canSeeDefectsMenu || canSeeWorkOrdersMenu) && (
                  <li
                    className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                      isSelectedOperationsTab
                        ? selectedTabStyle
                        : ""
                    }`}
                  >
                      <CustomDropdownMenu
                      label="Operations"
                      items={OperationsMenuItems()}
                      tooltip="View and manage defects and work orders"
                      placement="top"
                      isSelectedTab={isSelectedOperationsTab}
                    />
                   
                  </li>
                )}

                    {canSeeEquipmentMenu && (
                        <li
                          className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                            isSelectedEquipmentTab ? selectedTabStyle : ""
                          }`}
                        >
                          <CustomDropdownMenu
                            label="Mobile Unit Management"
                            items={getEquipmentMenuItems()}
                            tooltip="Manage mobile units"
                            placement="top"
                            isSelectedTab={isSelectedEquipmentTab}
                          />
                        </li>
                      )}

                    {canSeeAnalytics && (
                      <li
                        className={`transition-all duration-300 p-1 sm:p-2 rounded-[5px] whitespace-nowrap ${
                          analyticsPath.includes("/" + pathname.split("/")[1])
                            ? selectedTabStyle
                            : ""
                        }`}
                      >
                        <MuiTooltipWrapper
                          title="View Analytics"
                          placement="top"
                        >
                          <span
                            onClick={() => navigate("/analytics")}
                            className="block py-1 px-2 sm:py-2 sm:px-3 rounded md:hover:bg-transparent md:p-0 cursor-pointer text-xs sm:text-sm md:text-[14px] lg:text-[16px]"
                          >
                            Analytics
                          </span>
                        </MuiTooltipWrapper>
                      </li>
                    )}

                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area - This will expand to fill available space */}
      <main className="flex-grow flex flex-col">
        <Outlet />
      </main>

      {/* Footer - Sticky to bottom */}
      <Footer />

      {/* Snackbar for logout warning - only show if logged in */}
      {isLoggedIn && (
        <Snackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity="warning">
            {warningMessage}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
}
